import {zodResolver} from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useConfirm} from '@/components/confirm-dialog/useConfirm';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {FormSkeleton} from '@/components/ui/skeleton';
import {Textarea} from '@/components/ui/textarea';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useDeleteWorkTimeEntryMutation,
  useGetMyWorkTimeEntriesByDateRangeQuery,
  useGetWorkTimeEntriesByDateRangeQuery,
  useGetWorkTimeEntryQuery,
  useUpdateWorkTimeEntryMutation,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {TIME_FORMATS} from '@/utils/timeFormats';

import type {UpdateWorkTimeEntryFormValues} from '../utils/workTimeFormInputs';
import {updateWorkTimeEntryFormSchema} from '../utils/workTimeFormInputs';

export type EditWorkTimeEntryDialogProps = DialogProps;

function EditWorkTimeEntryDialog({
  onOpenChange,
  ...props
}: EditWorkTimeEntryDialogProps) {
  const {t} = useTranslation();
  const {toast} = useToast();
  const {confirm} = useConfirm();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('editWorkTimeEntry') || '';
  const entryQuery = useGetWorkTimeEntryQuery(
    graphqlClient,
    {id},
    {enabled: !!id}
  );
  const updateEntry = useUpdateWorkTimeEntryMutation(graphqlClient);
  const deleteEntry = useDeleteWorkTimeEntryMutation(graphqlClient);
  const form = useForm<UpdateWorkTimeEntryFormValues>({
    resolver: zodResolver(updateWorkTimeEntryFormSchema),
    defaultValues: {startedAt: '', endedAt: '', note: ''},
  });

  useEffect(() => {
    const entry = entryQuery.data?.getWorkTimeEntry;
    if (!entry) return;
    form.reset({
      startedAt: entry.startedAt,
      endedAt: entry.endedAt ?? '',
      note: entry.note || '',
    });
  }, [entryQuery.data, form]);

  const handleClose = (open: boolean) => {
    form.reset();
    onOpenChange?.(open);
  };

  const invalidateWorkTimeListQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: useGetMyWorkTimeEntriesByDateRangeQuery.getKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: useGetWorkTimeEntriesByDateRangeQuery.getKey(),
      }),
    ]);
  };

  const handleSubmit = async (values: UpdateWorkTimeEntryFormValues) => {
    try {
      await updateEntry.mutateAsync({input: {id, ...values}});
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: useGetWorkTimeEntryQuery.getKey({id}),
        }),
        invalidateWorkTimeListQueries(),
      ]);
      toast({title: t('Work time updated'), variant: 'success'});
      handleClose(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await confirm({
        title: t('Delete work time entry'),
        message: t('Are you sure you want to delete this work time entry?'),
        isWarning: true,
      });
      await deleteEntry.mutateAsync({id});
      await invalidateWorkTimeListQueries();
      toast({title: t('Work time entry deleted'), variant: 'success'});
      handleClose(false);
    } catch (error) {
      console.error(error);
    }
  };

  const hasEntry = Boolean(entryQuery.data?.getWorkTimeEntry);
  const isPending = updateEntry.isPending || deleteEntry.isPending;

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={handleClose}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Edit work time')}</DialogTitle>
            </DialogHeader>
            <DialogSection className="flex flex-col gap-4">
              {entryQuery.isLoading && <FormSkeleton size={3} />}
              {!entryQuery.isLoading && (
                <>
                  <FormRequiredLegend />

                  {(['startedAt', 'endedAt'] as const).map(name => (
                    <FormField
                      key={name}
                      control={form.control}
                      name={name}
                      render={({field}) => (
                        <FormItem>
                          <FormLabel required>
                            {name === 'startedAt'
                              ? t('Started at')
                              : t('Ended at')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="datetime-local"
                              value={dayjs(field.value || undefined).format(
                                TIME_FORMATS.dateTimeHTMLLocal
                              )}
                              onChange={event =>
                                field.onChange(
                                  dayjs(event.target.value).toISOString()
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                  <FormField
                    control={form.control}
                    name="note"
                    render={({field}) => (
                      <FormItem>
                        <FormLabel>{t('Note')}</FormLabel>
                        <FormControl>
                          <Textarea {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </DialogSection>
            <DialogFooter>
              {hasEntry && (
                <Button
                  type="button"
                  className="sm:mr-auto"
                  variant="warning"
                  disabled={isPending}
                  onClick={handleDelete}
                >
                  {t('Delete work time entry')}
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleClose(false)}
              >
                {t('Close')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={entryQuery.isLoading || isPending || !hasEntry}
              >
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
}

export default EditWorkTimeEntryDialog;
